import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { connect } from "react-redux";
import { withStyles } from "@mui/styles";
import moment from "moment";
import { Grid } from "@mui/material";
import { toast } from "react-toastify";

import Table from "../common/TableMultiCheckbox";
import TextField from "../common/TextField";
import Loader from "../../Layout/Loader";
import {
  checkPrivileges,
  prepareUserForLogin,
  PaginationConfig,
} from "../../Helpers";
import CustomButton from "../common/Button";

const styles = () => ({
  container: { display: "flex", flexWrap: "wrap" },
});

const UserAccessVehicle = (props) => {
  const cancelTokenRef = useRef(null);
const { newVehicleId, translate } = props;

  const [state, setState] = useState({
    usersReceived: false,
    selectedUnitsList: [],
    pagination: {
      ...PaginationConfig,
      pageSize: 25,
      page: 1,
      itemSearch: "",
    },
    loader: false,
    noRecords: false,
    isChecked: false,
    isIndeterminate: false,
  });
const unitToUserpermission = async (checked, item) => {
  if (!newVehicleId) {
    toast.error(translate("Vehicle not selected"));
    return;
  }

  // 🔹 Optimistic UI update
  setState((prev) => ({
    ...prev,
    selectedUnitsList: prev.selectedUnitsList.map((u) =>
      u.entity.id === item.id
        ? { ...u, entity: { ...u.entity, check: checked } }
        : u
    ),
  }));

  try {
    const res = await fetch("/api/permissions", {
      method: checked ? "POST" : "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        userId: item.id,
        vehicleId: newVehicleId,
      }),
    });

    if (!res.ok) {
      throw new Error(`HTTP ${res.status}`);
    }

    const data = await res.json();

    if (data?.status === "success") {
      toast.success(
        translate(
          checked
            ? "userIsAssignedSuccessfully"
            : "userIsUnAssignedSuccessfully"
        )
      );
    } else {
      throw new Error(data?.message || "Request failed");
    }
  } catch (error) {
    // 🔹 Rollback UI on error
    setState((prev) => ({
      ...prev,
      selectedUnitsList: prev.selectedUnitsList.map((u) =>
        u.entity.id === item.id
          ? { ...u, entity: { ...u.entity, check: !checked } }
          : u
      ),
    }));

    toast.error(error.message || translate("Something went wrong"));
  }
};
  // ================= API ERROR =================
  const handleApiError = useCallback(
    (res) => {
      if (res?.statusCode === "440") {
        window.location.replace("/login");
      } else {
        toast.error(res?.message || props.translate("Something went wrong"));
      }
    },
    [props]
  );

  // ================= FETCH =================
  const getMoreUnits = useCallback(async () => {
    const { pagination } = state;
    const { page, pageSize, itemSearch } = pagination;
    const { logInUser } = props;

    let url = `/api/users/get?userId=${logInUser.id}&all=true&page=${page}&limit=${-1}&search=${itemSearch}`;

    if (cancelTokenRef.current) {
      cancelTokenRef.current.cancel();
    }

    cancelTokenRef.current = axios.CancelToken.source();

    setState((prev) => ({ ...prev, loader: true }));

    try {
      const res = await axios.get(url, {
        cancelToken: cancelTokenRef.current.token,
      });

      const data = res.data;

      if (data.status !== "success") {
        return handleApiError(data);
      }

      const users = data?.data?.data || [];

      if (!users.length) {
        return setState((prev) => ({
          ...prev,
          selectedUnitsList: [],
          usersReceived: false,
          noRecords: true,
          loader: false,
        }));
      }

      const formattedUsers = users.map((item) => ({
        ...item,
        entity: {
          ...item.entity,
          contractNumber:item.extraInfo?.contractNumber,
          created: item.entity.created
            ? moment(item.entity.created).format("YYYY-MM-DD HH:mm")
            : "",
        },
      }));
      setState((prev) => ({
        ...prev,
        selectedUnitsList: prepareUserForLogin(formattedUsers),
        usersReceived: true,
        noRecords: false,
        pagination: {
          ...prev.pagination,
          total: data.data.total,
          hasNext: data.hasNext,
        },
      }));
    } catch (err) {
      if (!axios.isCancel(err)) {
        toast.error(props.translate("Error fetching users"));
      }
    } finally {
      setState((prev) => ({ ...prev, loader: false }));
    }
  }, [state.pagination, props, handleApiError]);

  // ================= EFFECT =================
  useEffect(() => {
    getMoreUnits();

    return () => {
      if (cancelTokenRef.current) {
        cancelTokenRef.current.cancel();
      }
    };
  }, [state.pagination.page, state.pagination.pageSize, state.pagination.itemSearch]);

  // ================= EVENTS =================
  const handleSearch = (value) => {
    setState((prev) => ({
      ...prev,
      pagination: {
        ...prev.pagination,
        page: 1,
        itemSearch: value,
      },
    }));
  };

  const handleChangePage = (page) => {
    setState((prev) => ({
      ...prev,
      pagination: { ...prev.pagination, page },
    }));
  };

  const handleChangeRowsPerPage = (pageSize) => {
    setState((prev) => ({
      ...prev,
      pagination: { ...prev.pagination, pageSize, page: 1 },
    }));
  };

  // ================= RENDER =================
  const { usersReceived, loader, noRecords } = state;

  return (
    <div>
      <Grid container alignItems="center" justifyContent="space-between">
        <Grid item xs={12} sm={4}>
          <h3 style={{ margin: 0 }}>
            {props.translate("userAccessVehicle")}
          </h3>
        </Grid>

        <Grid item xs={12} sm={2}>
          <TextField
            fullWidth
            value={state.pagination.itemSearch}
            label={props.translate("search")}
            onChange={(e) => handleSearch(e.target.value)}
          />
        </Grid>

        <Grid item xs={12} sm={4} sx={{ display: "flex", justifyContent: "flex-end" }}>
          <CustomButton onClick={props.handleFinish}>
            {props.translate("finish")}
          </CustomButton>
        </Grid>
      </Grid>

      {loader ? (
        <Loader defaultStyle />
      ) : usersReceived ? (
        <Table
          rows={(state.selectedUnitsList || [])?.map((i) => i?.entity)}
          pagination={state.pagination}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          handleChangePage={handleChangePage}
          showCheckbox
          disableHead
          UserAccessVehicle
          checkHandleChange ={unitToUserpermission}
          rowsPerPage={state.pagination.pageSize}
          canAssign={checkPrivileges("userLinkDevice")}
          canRemove={checkPrivileges("userUnlinkDevice")}
          rowDefinition={[
  { id: "name", label: props.translate("sharedName") },
  { id: "email", label: props.translate("userEmail") },
  { id: "contractNumber", label: props.translate("contractNumber") },
//   { id: "city", label: props.translate("city") },
  { id: "created", label: props.translate("createdAt") },
]}
        />
      ) : noRecords ? (
        <h4 style={{ textAlign: "center" }}>
          {props.translate("No Users Found")}
        </h4>
      ) : (
        <Loader defaultStyle />
      )}
    </div>
  );
};

export default connect()(withStyles(styles)(UserAccessVehicle));